import { TranslateModule } from '@ngx-translate/core';
import {
  ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, input, output, signal,
} from '@angular/core';

import { ICart } from '../../../cart/models/cart/i-cart';
import { CartServiceV2 } from '../../../cart/services/cart-v2.service';
import { RETURN_METHODS } from '../../models/return-packaging.constants';
import { IReturnScheduleDelivery } from '../../models/i-return-schedule-delivery';
import {
  getReturnSettings,
  isReturnLineOutdated,
  isReturnStepRequired,
  getEarliestReturnDate,
  getAvailableReturnMethods,
  getReturnPickupLocation,
} from '../../policy/return-packaging.policy';
import { ReturnPackagingStore } from '../../services/return-packaging.store';
import { getReturnPackagingErrorKey } from '../../helpers/return-packaging-error.helper';
import { ReturnMethodChoiceComponent } from '../return-method-choice/return-method-choice.component';
import { SelfReturnScheduleComponent } from '../self-return-schedule/self-return-schedule.component';
import { CourierReturnScheduleComponent } from '../courier-return-schedule/courier-return-schedule.component';

/**The whole return step, and the only piece an app mounts. It owns the store, so the customer's
 * choice lives and dies with the step, and it decides which schedule to show — the method choice is
 * skipped entirely when the seller offers only one.*/
@Component({
  standalone: true,
  selector: 'return-packaging-step',
  imports: [
    TranslateModule,
    ReturnMethodChoiceComponent,
    SelfReturnScheduleComponent,
    CourierReturnScheduleComponent,
  ],
  templateUrl: './return-packaging-step.component.html',
  styleUrls: ['./return-packaging-step.component.scss'],
  providers: [ReturnPackagingStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReturnPackagingStepComponent implements OnInit {
  readonly cart = input.required<ICart>();
  readonly nextBtnView = input<string>('btn-pink');
  readonly nextBtnTextKey = input<string>('orderProcess.delivery.goToPayment');

  /**The order line is saved by the time this fires — the host navigates to payment.*/
  readonly submitted = output<void>();
  /**Carries the translation key to show.*/
  readonly failed = output<string>();
  readonly noLongerRequired = output<void>();

  readonly store = inject(ReturnPackagingStore);

  private readonly _cartService = inject(CartServiceV2);

  readonly isSaving = signal(false);

  readonly settings = computed(() => getReturnSettings(this.cart()));
  /**Re-read from the live cart rather than settled on entry: the customer can empty the cart of
   * returnable products without ever leaving this step.*/
  readonly isStepRequired = computed(() => isReturnStepRequired(this.cart()));
  readonly availableMethods = computed(() => getAvailableReturnMethods(this.settings()));
  readonly hasChoice = computed(() => this.availableMethods().length > 1);

  readonly delivery = computed<IReturnScheduleDelivery>(() => this.cart().delivery);
  readonly earliestDate = computed(() => getEarliestReturnDate(this.delivery()));
  readonly bakeryId = computed(() => this.cart().bakery?.id);
  readonly countryCode = computed(() => this._country().code || '');
  readonly currency = computed(() => this._country().currency || '');

  readonly pickupLocation = computed(() => getReturnPickupLocation(this.cart()));

  /**Nothing to reuse means the customer has to give us a location, and the heading has to ask for it.*/
  readonly courierHeading = computed(() => this.pickupLocation()
    ? 'orderProcess.returnPackaging.courierReturn.heading'
    : 'orderProcess.returnPackaging.courierReturn.headingWithAddress');

  constructor() {
    effect(() => {
      if (!this.isStepRequired()) {
        this.noLongerRequired.emit();
      }
    });
  }

  /**Hydrating once on init rather than on every cart emission: the cart is re-dispatched for
   * unrelated reasons, and re-hydrating would snap a half-made choice back to the saved one.*/
  public ngOnInit(): void {
    const existing = this.cart().returnPackaging;

    if (existing && !isReturnLineOutdated(existing, this.delivery(), this.settings())) {
      this.store.hydrateFrom(existing);
      return;
    }

    const methods = this.availableMethods();

    if (methods.length === 1) {
      this.store.selectMethod(methods[0]);
    }
  }

  public onMethodChange(method: RETURN_METHODS): void {
    this.store.selectMethod(method);
  }

  public async submit(): Promise<void> {
    if (this.isSaving()) {
      return;
    }

    this.isSaving.set(true);

    try {
      await this._persistChoice();
      this.submitted.emit();
    } catch (error) {
      this.failed.emit(getReturnPackagingErrorKey(error));
    } finally {
      this.isSaving.set(false);
    }
  }

  /**The cart can stop needing a return while the step is open — removing the last returnable product
   * does it — and no route guard re-runs to catch it. Confirming then must not add a line the order
   * has no reason to carry, and must drop one already saved, or the customer is charged a pickup for
   * packaging they were never given.*/
  private _persistChoice(): Promise<ICart> {
    if (!this.isStepRequired()) {
      return this._cartService.removeReturnPackagingLine();
    }

    return this._cartService.saveReturnPackagingLine(this.store.details(), this.store.price());
  }

  private _country() {
    return this.cart()?.bakery?.country || { code: '', currency: '' };
  }
}
